import { useId, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { designTokens } from '../tokens';
import { HelpPopover } from './HelpPopover';

export type FormSectionProps = {
  heading: ReactNode;
  help?: ReactNode;
  helpTitle?: string;
  description?: ReactNode;
  children: ReactNode;
};

/** 관련 입력을 하나의 이름 있는 그룹으로 묶습니다. */
export function FormSection({ heading, help, helpTitle, description, children }: FormSectionProps) {
  const descriptionId = useId();
  const headingId = `${descriptionId}-heading`;
  const hasDescription = description !== undefined && description !== null && description !== '' && typeof description !== 'boolean';
  return <Box component="fieldset" aria-labelledby={headingId} aria-describedby={hasDescription ? descriptionId : undefined} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
    <Box component="legend" sx={{ p: 0, mb: `${designTokens.layout.formSectionGap}px`, maxWidth: '100%' }}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: `${designTokens.space.xxs}px`, maxWidth: '100%' }}>
        <Typography id={headingId} component="span" sx={{ ...designTokens.typography.subsectionTitle, minWidth: 0, overflowWrap: 'anywhere' }}>{heading}</Typography>
        <HelpPopover title={helpTitle ?? (typeof heading === 'string' || typeof heading === 'number' ? String(heading) : '입력 그룹')}>{help}</HelpPopover>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${designTokens.layout.formSectionGap}px`, minWidth: 0 }}>
      {hasDescription ? <Typography id={descriptionId} component="div" sx={{ ...designTokens.typography.supporting, color: designTokens.color.secondaryText, overflowWrap: 'anywhere' }}>{description}</Typography> : null}
      {children}
    </Box>
  </Box>;
}

export type FormGridProps = {
  columns?: 1 | 2;
  children: ReactNode;
};

/** 좁은 화면에서는 한 열, 넓은 화면에서는 지정한 열 수로 입력을 배치합니다. */
export function FormGrid({ columns = 2, children }: FormGridProps) {
  return <Box sx={{
    display: 'grid', minWidth: 0,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: columns === 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
    gap: `${designTokens.layout.formFieldGap}px`,
    '& > *': { minWidth: 0 },
  }}>{children}</Box>;
}

export type FormGridItemProps = {
  wide?: boolean;
  children: ReactNode;
  testId?: string;
};

/** 한 입력이나 관련 내용을 묶고 필요할 때 grid 전체 너비를 사용합니다. */
export function FormGridItem({ wide = false, children, testId }: FormGridItemProps) {
  return <Box data-testid={testId} sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>{children}</Box>;
}
